import { Row, Col, Badge } from "react-bootstrap";
import NiceModal from "@ebay/nice-modal-react";
import { useTranslation } from "react-i18next";
import { ProductImageURL } from "../../../components/custom/category/listings/ProductImage";

import { formatCurrency } from "../../../utils/formatNumber";
import ExternalProductLink from "../../../components/custom/merchandise/ExternalProductLink";
import CustomTag from "../../../components/core/blocks/CustomTag";
import { useAppSelector } from "../../../redux/hooks";
import { useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import BulkEditModal from "../../../components/core/basic/BulkEditModal";
import { EntityTagsFormModal } from "../../tags/components/EntityTagsFormModal";
import { EntityTagList } from "../../tags/components/EntityTagList";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { css } from "../../../../styled-system/css";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import type { TagsEnhancedItem } from "../../../zodios/common/tagsSchema";

type BuyboxReason = "DD" | "CP" | "HP" | "OOS" | "FM" | "UNK" | "REF";

interface Props {
  product: {
    product_image_url: string | null;
    product_title: string;
    asin: string;
    sku: string;
    tags: string[] | null;
    tags_enhanced?: Pick<TagsEnhancedItem, "id">[] | null;
    eligibility_status: "ELIGIBLE" | "INELIGIBLE" | "ELIGIBLE_WITH_WARNING" | null;
    buy_box: boolean;
    buy_box_reason: BuyboxReason | null;
  };
  inventoryBadgeText?: string;
  salesLabel?: string;
  pricingInfo?: { amazonStatus: string | null; productPrice: number };
  updateTags: (tags: string[] | number[]) => void;
}

export function ProductSummaryHeader({ product, inventoryBadgeText, salesLabel, pricingInfo, updateTags }: Props) {
  const { t } = useTranslation(["merchandising"]);
  const [showModal, setShowModal] = useState(false);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const marketPlace = useAppSelector((state) => state.marketPlace);

  const existingTagIds = useMemo(() => (product.tags_enhanced ?? []).map((t) => t.id), [product.tags_enhanced]);

  const removeTag = (tagId: number) => {
    updateTags(existingTagIds.filter((id) => id !== tagId));
  };

  const openAddModal = () => {
    void NiceModal.show(EntityTagsFormModal, {
      entityType: "PRODUCT",
      title: t(($) => $.merchandising["Edit Product Tags"]),
      initialTagIds: existingTagIds,
      onSubmit: (tagIds: number[]) => {
        updateTags(tagIds);
      },
    });
  };

  const buyboxReasonLabel = (reason: BuyboxReason): string => {
    switch (reason) {
      case "DD":
        return t(($) => $.merchandising["Delivery Date"]);
      case "CP":
        return t(($) => $.merchandising["Competitive Price"]);
      case "HP":
        return t(($) => $.merchandising["High Price"]);
      case "OOS":
        return t(($) => $.merchandising["Out of Stock"]);
      case "FM":
        return t(($) => $.merchandising["Not Features Merchant"]);
      case "REF":
        return t(($) => $.merchandising["Reference Price"]);
      case "UNK":
      default:
        return t(($) => $.merchandising["Unknown"]);
    }
  };

  return (
    <div style={{ display: "flex" }}>
      <div style={{ width: "120px", paddingRight: "20px" }}>
        <ProductImageURL url={product.product_image_url} style={{ borderRadius: 15 }} width={100} height={100} />
      </div>
      <div>
        <Row>
          <Col xs={12}>
            <h3
              style={{
                marginBottom: "0.2rem",
                marginTop: "0",
                fontSize: 22,
              }}
            >
              {product.product_title}
            </h3>
          </Col>
          <Col xs={12}>
            {product.asin} ({product.sku}) <ExternalProductLink asin={product.asin} />
          </Col>
          <Col xs={12}>
            <Row className="pt-3">
              <Col>
                <div className="pe-5 text-center">
                  <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                    {t(($) => $.merchandising["Inventory"])}
                  </h6>
                  <Badge
                    className="badge bg-trellis-purple"
                    style={{
                      marginRight: "0.5rem",
                      backgroundColor: "#c43066",
                    }}
                    pill
                  >
                    {inventoryBadgeText ? inventoryBadgeText : t(($) => $.merchandising["unknown"])}
                  </Badge>
                </div>
              </Col>
              {salesLabel && (
                <Col>
                  <div className="pe-5 text-center">
                    <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                      {t(($) => $.merchandising["Life Cycle"])}
                    </h6>
                    <Badge className="badge bg-trellis-fuchsia" pill>
                      {salesLabel}
                    </Badge>
                  </div>
                </Col>
              )}
              {pricingInfo && (
                <Col>
                  <div className="pe-5 text-center">
                    <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                      {t(($) => $.merchandising["Listing Status"])}
                    </h6>
                    <Badge className="badge bg-trellis-fuchsia" pill>
                      {pricingInfo.amazonStatus ?? t(($) => $.merchandising["unknown"])}
                    </Badge>
                  </div>
                </Col>
              )}
              {pricingInfo?.productPrice ?
                <Col>
                  <div className="pe-5 text-center">
                    <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3 text-nowrap">
                      {t(($) => $.merchandising["Price"])}
                    </h6>

                    <Badge
                      className={`badge bg-trellis-lightgrey text-end`}
                      style={{
                        borderRadius: "5px",
                      }}
                    >
                      {formatCurrency(pricingInfo.productPrice, {
                        marketPlace: marketPlace.marketPlace,
                      })}
                    </Badge>
                  </div>
                </Col>
              : null}
              {product.eligibility_status && (
                <Col>
                  <div className="pe-5 text-center">
                    <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3 text-nowrap">
                      {t(($) => $.merchandising["Buy Box"])}
                    </h6>

                    <Badge
                      className={`badge  bg-trellis-fuchsia rounded-pill text-end`}
                      style={{
                        borderRadius: "5px",
                      }}
                    >
                      {product.buy_box ?
                        t(($) => $.merchandising["Yes"])
                      : buyboxReasonLabel(product.buy_box_reason ?? "UNK")}
                    </Badge>
                  </div>
                </Col>
              )}

              <Col>
                <div className="pe-5 text-center">
                  {tagsEnhancedEnabled ?
                    <div className="d-flex pt-1 justify-content-center gap-2 align-items-center">
                      <EntityTagList
                        entityType="PRODUCT"
                        tagIds={existingTagIds}
                        onRemove={removeTag}
                        ariaLabel={t(($) => $.merchandising["Product tags"])}
                      />
                      <span className="d-flex align-items-center" style={{ cursor: "pointer" }}>
                        <HoverInfo icon={<FaPlus className={css({ cursor: "pointer" })} onClick={openAddModal} />}>
                          {t(($) => $.merchandising["Add Product Tags"])}
                        </HoverInfo>
                      </span>
                    </div>
                  : <div className="d-flex pt-1 justify-content-center gap-2">
                      {product.tags?.map((tag) => (
                        <CustomTag
                          tag={tag}
                          key={tag}
                          onUpdate={(tag) => {
                            updateTags(
                              product.tags?.filter((tagToDelete) => tag.toLowerCase() !== tagToDelete.toLowerCase()) ??
                                [],
                            );
                          }}
                        />
                      ))}

                      <span className="d-flex align-items-center" style={{ cursor: "pointer" }}>
                        <HoverInfo
                          icon={<FaPlus className={css({ cursor: "pointer" })} onClick={() => setShowModal(true)} />}
                        >
                          {t(($) => $.merchandising["Add Product Tags"])}
                        </HoverInfo>
                      </span>

                      <BulkEditModal
                        title={t(($) => $.merchandising["Edit Product Tags"])}
                        isOpen={showModal}
                        onClose={() => setShowModal(false)}
                        defaultValues={product.tags ?? []}
                        onSave={(values) => {
                          updateTags(values);
                        }}
                      />
                    </div>
                  }
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
}
